import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef } from 'react';
import { AdminDialogProperty } from './AdminSettings';

export interface AdminSettingFormRef {
  getValues: () => any;
  triggerValidation: () => Promise<boolean>;
}

interface AdminSettingsDialogProps {
  open: boolean;
  settingsInfo: AdminDialogProperty;

  handleClose: () => void;
}

const PREFIX = 'AdminSettingsDialog';

const classes = {
  subtitle: `${PREFIX}-subtitle`,
  description: `${PREFIX}-description`,
  actionButton: `${PREFIX}-actionButton`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root ': {
    ...theme.typography.h3,
  },
  '& .MuiDialog-paper': {
    width: 444,
  },
  '& .MuiDialogActions-root': {
    marginTop: theme.spacing(1),
  },
  [`& .${classes.description}`]: {
    ...theme.typography.h5,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.actionButton}`]: {
    width: 111.5,
    height: 48,
  },
}));

export function AdminSettingsDialog({
  open,
  settingsInfo,
  handleClose,
}: AdminSettingsDialogProps) {
  const { t } = useTranslation('common');
  const formRef = useRef<AdminSettingFormRef>(null);
  const { settingId, title, description, form, saveHandler } = settingsInfo;

  const handleSave = async () => {
    const valid = await formRef.current?.triggerValidation();
    if (!valid) {
      return;
    }

    const values = formRef.current?.getValues();
    saveHandler(values);
    handleClose();
  };

  return (
    <StyledDialog open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography className={classes.description}>{description}</Typography>
        {form(formRef)}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          className={classes.actionButton}
          onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          className={classes.actionButton}
          onClick={handleSave}
          autoFocus>
          {t('common.save')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
